import React, { useRef } from 'react';

import { avatarsFromAvatarUrl } from '@trello/members';
import { currentLocale } from '@trello/locale';
import { Tooltip, Button } from '@trello/nachos';
import { EnterpriseDashboardTestIds } from '@trello/webdriverio-tests';
import { Spinner } from 'app/src/components/Spinner';

import { MemberAvatarUnconnected } from 'app/src/components/MemberAvatar';
import { forTemplate, localizeCount } from 'app/common/lib/util/i18n';
import { Popover, usePopover } from 'app/gamma/src/components/popover';
import { TeamLogo } from './TeamLogo';
import styles from './PendingTeamItem.less';
const format = forTemplate('enterprise_pending_teams');

interface PendingTeamMember {
  id: string;
  fullName: string;
  avatarUrl?: string;
  username: string;
  initials?: string;
}

export interface PendingTeam {
  id: string;
  displayName: string;
  requestorName: string;
  requestDate: Date;
  logoUrl?: string;
  numMembers: number;
  newBillableMembers: PendingTeamMember[];
  restrictedMembers: PendingTeamMember[];
}

interface PendingTeamItemProps extends PendingTeam {
  onApprove: () => void;
  onDecline: () => void;
  isLoading: boolean;
  enterprise: {
    totalMembers: number;
    maxMembers: number | null;
  };
}

export const PendingTeamItem = (props: PendingTeamItemProps) => {
  const {
    id,
    displayName,
    requestorName,
    requestDate,
    numMembers,
    logoUrl,
    newBillableMembers,
    restrictedMembers,
    onApprove,
    onDecline,
    isLoading,
    enterprise,
  } = props;

  const newBillableRef = useRef(null);
  const restrictedRef = useRef(null);

  const newBillablePopoverName = `NEW_BILLABLE_POPOVER${id}`;
  const restrictedPopoverName = `RESTRICTED_POPOVER${id}`;

  const {
    isPopoverShown: isNewBillablePopoverShown,
    showPopover: showNewBillablePopover,
    closePopover: closeNewBillablePopover,
  } = usePopover({
    name: newBillablePopoverName,
  });

  const {
    isPopoverShown: isRestrictedPopoverShown,
    showPopover: showRestrictedPopover,
    closePopover: closeRestrictedPopover,
  } = usePopover({
    name: restrictedPopoverName,
  });

  const newBillableText = format('new-billable', {
    numNewBillable: newBillableMembers.length,
  });

  const restrictedText = format('restricted', {
    numRestricted: restrictedMembers.length,
  });

  const newBillableComponent =
    newBillableMembers.length > 0 ? (
      <a
        href="#"
        onClick={showNewBillablePopover}
        data-test-id={`${EnterpriseDashboardTestIds.NewBillableMembers}`}
        ref={newBillableRef}
      >
        {newBillableText}
      </a>
    ) : (
      <span data-test-id={`${EnterpriseDashboardTestIds.NewBillableMembers}`}>
        {newBillableText}
      </span>
    );

  const restrictedComponent =
    restrictedMembers.length > 0 ? (
      <a
        href="#"
        onClick={showRestrictedPopover}
        data-test-id={`${EnterpriseDashboardTestIds.RestrictedMembers}`}
        ref={restrictedRef}
      >
        {restrictedText}
      </a>
    ) : (
      <span data-test-id={`${EnterpriseDashboardTestIds.RestrictedMembers}`}>
        {restrictedText}
      </span>
    );

  const wouldExceedSeatCount = Boolean(
    enterprise.maxMembers &&
      enterprise.totalMembers + newBillableMembers.length >
        enterprise.maxMembers,
  );

  let approveButton = (
    <Button
      onClick={onApprove}
      data-test-id={EnterpriseDashboardTestIds.PendingTeamItemApproveButton}
      isDisabled={wouldExceedSeatCount}
    >
      {format('approve')}
    </Button>
  );

  if (enterprise.maxMembers && wouldExceedSeatCount) {
    approveButton = (
      <Tooltip
        content={format('need-to-purchase-more-licenses', {
          numNewLicenses: localizeCount(
            'new licenses',
            enterprise.totalMembers +
              newBillableMembers.length -
              enterprise.maxMembers,
          ),
        })}
        tag="span"
      >
        {approveButton}
      </Tooltip>
    );
  }

  return (
    <>
      <div
        className={styles.pendingTeamItem}
        data-test-id={`${EnterpriseDashboardTestIds.PendingTeamItem}${id}`}
      >
        <TeamLogo className={styles.teamAvatar} size={32} url={logoUrl} />
        <div className={styles.requestInfo}>
          <span className={styles.teamName}>{displayName}</span>
          <span className={styles.requestor}>
            {format('requested-by', {
              requestorName,
              date: requestDate.toLocaleDateString(currentLocale, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              }),
            })}
          </span>
        </div>
        <div className={styles.members}>
          <span>{format('members', { numMembers })}</span>
          <span className={styles.membersDetails}>
            {newBillableComponent},&ensp;{restrictedComponent}
          </span>
        </div>
        <div className={styles.actions}>
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              <Button
                onClick={onDecline}
                data-test-id={
                  EnterpriseDashboardTestIds.PendingTeamItemDeclineButton
                }
              >
                {format('decline')}
              </Button>
              {approveButton}
            </>
          )}
        </div>
      </div>
      {isNewBillablePopoverShown && (
        <Popover
          name={newBillablePopoverName}
          title={format('new-billable-members-popover-title')}
          target={() => newBillableRef.current}
          onClose={closeNewBillablePopover}
        >
          {() => (
            <span
              data-test-id={`${EnterpriseDashboardTestIds.NewBillableMembersList}`}
            >
              <MembersList members={newBillableMembers} />
            </span>
          )}
        </Popover>
      )}
      {isRestrictedPopoverShown && (
        <Popover
          name={restrictedPopoverName}
          title={format('restricted-members-popover-title')}
          target={() => restrictedRef.current}
          onClose={closeRestrictedPopover}
        >
          {() => (
            <span
              data-test-id={`${EnterpriseDashboardTestIds.RestrictedMembersList}`}
            >
              <MembersList members={restrictedMembers} />
            </span>
          )}
        </Popover>
      )}
    </>
  );
};

interface MembersListProps {
  members: PendingTeamMember[];
}

const MembersList: React.FunctionComponent<MembersListProps> = ({
  members,
}: MembersListProps) => {
  return (
    <div>
      {members.map((m) => (
        <div className={styles.member} key={m.id}>
          <span className={styles.memberAvatar}>
            <MemberAvatarUnconnected
              avatars={
                m.avatarUrl ? avatarsFromAvatarUrl(m.avatarUrl) : undefined
              }
              initials={m.initials}
            />
          </span>
          <p className={styles.fullName}>{m.fullName}</p>
          <span className={styles.usernameWrapper}>
            <span>(</span>
            <p className={styles.username}>{m.username}</p>
            <span>)</span>
          </span>
        </div>
      ))}
    </div>
  );
};
